import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Add as AddIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  OpenInBrowser as OpenInBrowserIcon,
  OpenInNew as OpenInNewIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { RoutePaths } from '@shared/config/routePathsEnum';
import { appStore } from '@shared/model/app_store/AppStore';
import { getBearerToken } from '@shared/utils/cookie_manager';

import { DialogsApi, type UnreadDialog } from '../api/dialogsApi';
import {
  CHAT_DESKTOP_POPUP_CLOSED_EVENT_KEY,
  CHAT_MAIN_RESTORE_SKIP_EMPTY_CLOSE_ONCE_LOCAL_KEY,
  CHAT_MAIN_RESTORE_SKIP_EMPTY_CLOSE_ONCE_SESSION_KEY,
  CHAT_POPUP_ACTIVE_STORAGE_KEY,
} from '../chatPopup/constants';
import {
  persistMainRestoreFromPopupState,
  persistMainToOperatorPopupHandoff,
} from '../chatPopup/mainChatOpenRestoreFromPopup';
import { isBrowserWebChatShell, isElectronChatShell, isElectronMainChatHost } from '../chatPopup/chatShellEnvironment';
import {
  DESKTOP_AUTH_READY_EVENT,
} from '../chatPopup/electronPopupAuth';
import { DESKTOP_BRANCH_READY_EVENT } from '../chatPopup/electronPopupSessionBootstrap';
import {
  ELECTRON_POPUP_REQUEST_UNREAD_REST_EVENT,
} from '../chatPopup/electronPopupUnreadRest';
import {
  closeOperatorChatPopupAndRestoreMain,
  openOperatorChatPopup,
} from '../chatPopup/openOperatorChatPopup';
import {
  OPERATOR_CHAT_POPUP_DOCK_EDGE_MARGIN_PX,
  POPUP_DOCK_PANEL_H_PX,
  POPUP_DOCK_PANEL_W_PX,
} from '../chatPopup/operatorChatPopupLayout';
import { writeOperatorChatPopupFrameLock } from '../chatPopup/operatorChatPopupFrameLock';
import { CHAT_POPUP_HEARTBEAT_MS, readMainChatFooterSuppressedByPopup } from '../chatPopup/popupPresence';
import {
  CHAT_POPUP_LAYOUT_PINNED,
  chatPanelDockStorageKeys,
  readChatLayoutPinned,
  writeChatLayoutPinned,
} from '../chatPopup/popupLayoutStorage';
import ChatPanel from '../components/ChatPanel';
import { ChatProvider, useChat } from '../contexts/ChatContext';
import { SocketProvider, useSocket } from '../contexts/SocketContext';
import { operatorUnreadDebug, unreadMapSnapshot } from '../lib/operatorUnreadDebugLog';
import {
  getCurrentOperatorBranchId,
  isPayloadForCurrentOperatorBranch,
  sessionBelongsToCurrentOperatorBranch,
} from '../lib/chatBranchGuard';
import { resolveSessionDialogIdForUnread } from '../lib/resolveSessionDialogIdForUnread';
import styles from './ChatFooter.module.scss';
import { getLayoutViewportSize } from './chatDockViewport';
import { type ChatFooterPanelSize, ChatFooterResizableFrame } from './ChatFooterResizableFrame';

/** Должно совпадать с медиазапросом скрытия `.minimizedChats` в ChatFooter.module.scss */
const CHAT_COMPACT_MINIMIZED_QUERY = '(max-width: 1024px)';

/** Геометрия `.chatFooter` / `ChatFooterResizableRoot`: превью левее развёрнутого окна с зазором. */
const CHAT_FOOTER_RIGHT = 80;
const CHAT_FOOTER_BOTTOM = 80;

function defaultDockMargins(isOperatorChatPopup: boolean): { r: number; b: number } {
  if (isOperatorChatPopup) {
    return {
      r: OPERATOR_CHAT_POPUP_DOCK_EDGE_MARGIN_PX,
      b: OPERATOR_CHAT_POPUP_DOCK_EDGE_MARGIN_PX,
    };
  }
  return { r: CHAT_FOOTER_RIGHT, b: CHAT_FOOTER_BOTTOM };
}
const MINIMIZED_PREVIEW_GAP_PX = 28;
const DEFAULT_CHAT_PANEL: ChatFooterPanelSize = { w: 520, h: 660 };
const MIN_CHAT_PANEL: ChatFooterPanelSize = { w: 360, h: 320 };
const PANEL_VIEW_MARGIN_PX = 40;
/** Минимальная полоска dock остаётся в окне при перетаскивании (в т.ч. к краю второго монитора в широком окне). */
const DOCK_VISIBILITY_STRIP_PX = 48;
const DOCK_PREVIEW_ROW_APPROX_PX = 68;
/** Колонка FAB (+ и переключатель чата) справа от панели внутри dock */
const DOCK_FAB_COLUMN_W_PX = 56;
const DOCK_FAB_STACK_H_PX = 196;

function readSavedPanelSize(isOperatorChatPopup: boolean): ChatFooterPanelSize | null {
  if (typeof window === 'undefined') return null;
  const k = chatPanelDockStorageKeys(isOperatorChatPopup);
  try {
    const w = parseInt(localStorage.getItem(k.panelW) || '', 10);
    const h = parseInt(localStorage.getItem(k.panelH) || '', 10);
    if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
    return { w, h };
  } catch {
    return null;
  }
}

function readSavedDockMargins(isOperatorChatPopup: boolean): { r: number; b: number } | null {
  if (typeof window === 'undefined') return null;
  const k = chatPanelDockStorageKeys(isOperatorChatPopup);
  try {
    const r = parseInt(localStorage.getItem(k.dockR) || '', 10);
    const b = parseInt(localStorage.getItem(k.dockB) || '', 10);
    if (!Number.isFinite(r) || !Number.isFinite(b)) return null;
    return { r, b };
  } catch {
    return null;
  }
}

function readInitialDockMargins(isOperatorChatPopup: boolean): { r: number; b: number } {
  const defaults = defaultDockMargins(isOperatorChatPopup);
  const saved = readSavedDockMargins(isOperatorChatPopup);
  if (saved) {
    return saved;
  }
  return defaults;
}

/** Желаемый размер панели (default / LS / ручной ресайз), без автоклампа по viewport. */
function readDesiredPanelSize(isOperatorChatPopup: boolean): ChatFooterPanelSize {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_CHAT_PANEL };
  }
  const isPopupPath = window.location.pathname === RoutePaths.operatorChatPopup;
  if (isPopupPath && isBrowserWebChatShell()) {
    return { w: POPUP_DOCK_PANEL_W_PX, h: POPUP_DOCK_PANEL_H_PX };
  }
  const saved = readSavedPanelSize(isOperatorChatPopup);
  if (saved) {
    return {
      w: Math.max(MIN_CHAT_PANEL.w, saved.w),
      h: Math.max(MIN_CHAT_PANEL.h, saved.h),
    };
  }
  return { ...DEFAULT_CHAT_PANEL };
}

function clampPanelSize(size: ChatFooterPanelSize): ChatFooterPanelSize {
  if (typeof window === 'undefined') return size;
  const { width: vw, height: vh } = getLayoutViewportSize();
  const maxW = Math.max(MIN_CHAT_PANEL.w, vw - CHAT_FOOTER_RIGHT - PANEL_VIEW_MARGIN_PX);
  const maxH = Math.max(MIN_CHAT_PANEL.h, vh - CHAT_FOOTER_BOTTOM - PANEL_VIEW_MARGIN_PX);
  return {
    w: Math.min(maxW, Math.max(MIN_CHAT_PANEL.w, size.w)),
    h: Math.min(maxH, Math.max(MIN_CHAT_PANEL.h, size.h)),
  };
}

/** Ширина `.chatFloatingDock` без колонки развёрнутых панелей: FAB + 2×gap + опционально колонка превью. */
function dockNonPanelWidthPx(previewCount: number): number {
  const previewColW = previewCount > 0 ? 260 : 0;
  return DOCK_FAB_COLUMN_W_PX + 2 * MINIMIZED_PREVIEW_GAP_PX + previewColW;
}

function clampPanelSizeForFloatingDock(
  size: ChatFooterPanelSize,
  args: {
    previewCount: number;
    expandedCount: number;
    dockRightPx: number;
    dockBottomPx: number;
  },
): ChatFooterPanelSize {
  if (typeof window === 'undefined') return size;
  const { width: vw, height: vh } = getLayoutViewportSize();
  const { previewCount, expandedCount, dockRightPx, dockBottomPx } = args;
  const nonPanelW = dockNonPanelWidthPx(previewCount);
  const maxW = Math.max(MIN_CHAT_PANEL.w, vw - dockRightPx - PANEL_VIEW_MARGIN_PX - nonPanelW);

  const maxDockH = Math.max(MIN_CHAT_PANEL.h, vh - dockBottomPx - PANEL_VIEW_MARGIN_PX);
  const previewStackH =
    previewCount > 0 ? previewCount * DOCK_PREVIEW_ROW_APPROX_PX + (previewCount - 1) * 8 : 0;

  let maxH = maxDockH;
  if (expandedCount > 0) {
    const sideColumnsH = Math.max(DOCK_FAB_STACK_H_PX, previewStackH);
    if (sideColumnsH <= maxDockH) {
      const maxFromPanels = (maxDockH - (expandedCount - 1) * 8) / expandedCount;
      maxH = Math.max(MIN_CHAT_PANEL.h, Math.floor(maxFromPanels));
    }
  }

  return {
    w: Math.min(maxW, Math.max(MIN_CHAT_PANEL.w, size.w)),
    h: Math.min(maxH, Math.max(MIN_CHAT_PANEL.h, size.h)),
  };
}

function getMaxPanelSizeForFloatingDock(args: {
  previewCount: number;
  expandedCount: number;
  dockRightPx: number;
  dockBottomPx: number;
}): ChatFooterPanelSize {
  if (typeof window === 'undefined') return { ...DEFAULT_CHAT_PANEL };
  return clampPanelSizeForFloatingDock({ w: 100000, h: 100000 }, args);
}

function clampDockMargins(
  r: number,
  b: number,
  dockW: number,
  dockH: number,
): { r: number; b: number } {
  if (typeof window === 'undefined') return { r, b };
  const { width: vw, height: vh } = getLayoutViewportSize();
  const strip = DOCK_VISIBILITY_STRIP_PX;
  const maxR = vw - strip;
  const maxB = vh - strip;
  const minR = -dockW + strip;
  const minB = -dockH + strip;
  return {
    r: Math.min(maxR, Math.max(minR, r)),
    b: Math.min(maxB, Math.max(minB, b)),
  };
}

function normalizeSessionDialogId(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === 'assigned') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function hasRenderableMinimizedContent(session: {
  selectedUsers?: unknown[];
  selectedUserName?: unknown;
  selectedDialog?: { id?: unknown } | null;
  assignedDialogId?: unknown;
  messages?: unknown[];
}): boolean {
  const hasSelectedUser = (session.selectedUsers?.length ?? 0) > 0;
  const hasSelectedUserName = typeof session.selectedUserName === 'string' && session.selectedUserName.trim() !== '';
  const selectedDialogId = session.selectedDialog?.id;
  const hasSelectedDialogId = Boolean(
    selectedDialogId != null &&
      String(selectedDialogId) !== '' &&
      String(selectedDialogId) !== '0' &&
      String(selectedDialogId) !== 'assigned',
  );
  const assignedDialogId = session.assignedDialogId;
  const hasAssignedDialogId = Boolean(
    assignedDialogId != null &&
      String(assignedDialogId) !== '' &&
      String(assignedDialogId) !== '0' &&
      String(assignedDialogId) !== 'assigned',
  );
  const hasMessages = (session.messages?.length ?? 0) > 0;
  return hasSelectedUser || hasSelectedUserName || hasSelectedDialogId || hasAssignedDialogId || hasMessages;
}

/** Боковое превью — только сессии с конкретным пользователем (не «Новый чат»). */
function hasConcreteUserForSidePreview(session: {
  selectedUsers?: unknown[];
  selectedUserName?: unknown;
  selectedDialog?: { client_name?: unknown; clientName?: unknown } | null;
}): boolean {
  const hasSelectedUserName =
    typeof session.selectedUserName === 'string' && session.selectedUserName.trim() !== '';
  const clientName = session.selectedDialog?.client_name ?? session.selectedDialog?.clientName;
  const hasClientName = typeof clientName === 'string' && clientName.trim() !== '';
  const hasSelectedUser = (session.selectedUsers?.length ?? 0) > 0;
  return hasSelectedUserName || hasClientName || hasSelectedUser;
}

function shouldShowSessionInSidePreview(session: {
  selectedUsers?: unknown[];
  selectedUserName?: unknown;
  selectedDialog?: { id?: unknown; client_name?: unknown; clientName?: unknown } | null;
  assignedDialogId?: unknown;
  messages?: unknown[];
  isMinimized?: boolean;
  usersCache?: Map<unknown, unknown>;
}): boolean {
  if (!sessionBelongsToCurrentOperatorBranch(session)) return false;
  return hasRenderableMinimizedContent(session) && hasConcreteUserForSidePreview(session);
}

/** Диалог уже открыт/привязан к какой‑либо сессии — не показывать его второй раз в превью «непрочитанных». */
function sessionListAlreadyCoversDialog(
  sessions: Array<{ selectedDialog?: { id?: unknown }; assignedDialogId?: unknown }>,
  dialogId: number,
): boolean {
  return sessions.some((session) => {
    const fromSelected = normalizeSessionDialogId(session.selectedDialog?.id);
    const fromAssigned = normalizeSessionDialogId(session.assignedDialogId);
    return fromSelected === dialogId || fromAssigned === dialogId;
  });
}

/**
 * Список непрочитанных с API кладётся в unreadDialogs у каждой сессии (loadUnreadDialogs).
 * Без дедупликации один dialog.id даёт по строке превью на каждую сессию — на мобильном список и счётчик врут.
 */
function collectDedupedUnreadDialogsForPreview(
  sessions: Array<{
    id: string;
    isMinimized?: boolean;
    unreadDialogs?: UnreadDialog[];
    selectedDialog?: { id?: unknown };
    assignedDialogId?: unknown;
  }>,
  hasSessionWithUser: (userId: number) => boolean,
): { dialog: UnreadDialog; sessionId: string }[] {
  const seenDialogIds = new Set<number>();
  const rows: { dialog: UnreadDialog; sessionId: string }[] = [];

  const orderedSessions = [...sessions].sort((a, b) => {
    const ma = !!a.isMinimized;
    const mb = !!b.isMinimized;
    if (ma === mb) return 0;
    return ma ? 1 : -1;
  });

  for (const session of orderedSessions) {
    const unreadList =
      session.unreadDialogs?.filter((dialog) => {
        if (!isPayloadForCurrentOperatorBranch(dialog)) return false;
        const dialogUserId = dialog.owner?.id;
        if (dialogUserId && hasSessionWithUser(dialogUserId)) return false;
        if (sessionListAlreadyCoversDialog(sessions, dialog.id)) return false;
        return true;
      }) ?? [];

    for (const dialog of unreadList) {
      if (seenDialogIds.has(dialog.id)) continue;
      const coveredByExpanded = sessions.some(
        (s) =>
          !s.isMinimized &&
          (normalizeSessionDialogId(s.selectedDialog?.id) === dialog.id ||
            normalizeSessionDialogId(s.assignedDialogId) === dialog.id),
      );
      if (coveredByExpanded) continue;
      seenDialogIds.add(dialog.id);
      rows.push({ dialog, sessionId: session.id });
    }
  }

  return rows;
}

function previewLineFromMessagePayload(msg: unknown, attachmentLabel: string): string {
  if (!msg || typeof msg !== 'object') return '';
  const m = msg as { text?: string; attachments?: unknown[] };
  const text = (m.text || '').trim();
  if (text.length > 0) return text;
  if (Array.isArray(m.attachments) && m.attachments.length > 0) return attachmentLabel;
  return '';
}

function truncatePreviewLine(text: string, maxLen: number): string {
  const s = text.trim();
  if (!s) return '';
  return s.length <= maxLen ? s : `${s.slice(0, maxLen)}…`;
}

/** Сравнение id филиала из zustand (number | string) без ложных «смен» при 5 vs "5". */
function normalizeBranchIdForChatClear(raw: unknown): string | null {
  if (raw === undefined || raw === null || raw === '') return null;
  return String(raw);
}

function unreadCountForPreviewEntry(
  dialog: UnreadDialog,
  dialogsUnreadCounts: Map<number, number> | undefined,
): number {
  const map = dialogsUnreadCounts || new Map();
  const fromApi = Number(dialog.countUnMessages ?? dialog.countUnreadMess ?? 0);
  const apiSafe = Number.isFinite(fromApi) ? fromApi : 0;
  const isClosed = String((dialog as any)?.status ?? '').toUpperCase() === 'CLOSED';
  // Есть явная запись по этому диалогу — только она (per-dialog карта авторитетна).
  if (map.has(dialog.id)) {
    const fromMap = map.get(dialog.id)!;
    // Для CLOSED backend snapshot по непрочитанным может быть свежее детальной карты WS.
    // Не даём занижать бейдж превью (кейс: map=1, фактически/API=7).
    return isClosed ? Math.max(fromMap, apiSafe) : fromMap;
  }
  return apiSafe;
}

/**
 * Счётчик на превью свёрнутой сессии: dialogId из ленты (если однозначен), иначе метаданные;
 * если в WS-карте есть запись для этого id — она приоритетнее session.unreadCount.
 */
function effectiveMinimizedSessionUnread(
  session: {
    selectedDialog?: { id?: unknown };
    assignedDialogId?: unknown;
    unreadCount?: number;
    messages?: any[];
    unreadDialogs?: UnreadDialog[];
  },
  dialogsUnreadCounts: Map<number, number> | undefined,
): number {
  const dialogId = resolveSessionDialogIdForUnread(session);
  const local = session.unreadCount ?? 0;
  const fromMessages =
    dialogId != null
      ? (session.messages ?? []).reduce((acc: number, msg: any) => {
          const mid = Number(msg.dialogId ?? msg.dialog?.id ?? NaN);
          if (mid !== Number(dialogId)) return acc;
          if (msg.messageStatus !== 'TO_OPERATOR') return acc;
          if (msg.is_read) return acc;
          if (String(msg.confirmStatus ?? '').toUpperCase() === 'READ') return acc;
          return acc + 1;
        }, 0)
      : 0;
  const fromUnreadDialogsApi =
    dialogId != null
      ? Number(
          session.unreadDialogs?.find((d) => Number(d.id) === Number(dialogId))?.countUnMessages ??
            session.unreadDialogs?.find((d) => Number(d.id) === Number(dialogId))
              ?.countUnreadMess ??
            0,
        )
      : 0;
  const apiSafe = Number.isFinite(fromUnreadDialogsApi) ? fromUnreadDialogsApi : 0;
  const map = dialogsUnreadCounts;
  if (dialogId != null && map != null && map.has(dialogId)) {
    const fromMap = map.get(dialogId)!;
    // Electron: WS-карта в popup может кратковременно содержать 0 при живом session.unreadCount / ленте.
    if (isElectronChatShell() && fromMap === 0 && Math.max(local, apiSafe, fromMessages) > 0) {
      return Math.max(local, apiSafe, fromMessages);
    }
    return Math.max(fromMap, local, apiSafe, fromMessages);
  }
  return Math.max(local, apiSafe, fromMessages);
}

function unreadInSessionMessagesByDialog(
  session: { messages?: any[] } | undefined,
  dialogId: number,
): number {
  if (!session?.messages?.length) return 0;
  return session.messages.reduce((acc: number, msg: any) => {
    const mid = Number(msg.dialogId ?? msg.dialog?.id ?? NaN);
    if (mid !== Number(dialogId)) return acc;
    if (msg.messageStatus !== 'TO_OPERATOR') return acc;
    if (msg.is_read) return acc;
    if (String(msg.confirmStatus ?? '').toUpperCase() === 'READ') return acc;
    return acc + 1;
  }, 0);
}

function minimizedSessionPreviewRaw(
  session: {
    messages?: any[];
    selectedDialog?: { id?: unknown };
    assignedDialogId?: unknown;
  },
  fetchedByDialogId: Record<number, string>,
  attachmentLabel: string,
): string {
  if (session.messages?.length) {
    const last = session.messages[session.messages.length - 1];
    const local = previewLineFromMessagePayload(last, attachmentLabel);
    if (local) return local;
  }
  const did = normalizeSessionDialogId(session.selectedDialog?.id ?? session.assignedDialogId);
  if (did !== null) {
    const fetched = (fetchedByDialogId[did] || '').trim();
    if (fetched) return fetched;
  }
  return '';
}

function unreadCountFromDialogRecord(dialog: UnreadDialog | Record<string, unknown>): number {
  const rec = dialog as Record<string, unknown>;
  const preferred = [
    rec.countUnMessages,
    rec.countUnreadMess,
    rec.countMessages,
    rec.countUnreadMessages,
    rec.unreadCount,
    rec.unread,
  ];
  for (const value of preferred) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  for (const [key, value] of Object.entries(rec)) {
    if (typeof value !== 'number' && typeof value !== 'string') continue;
    if (!/count|unread/i.test(key)) continue;
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function sumUnreadDialogCounts(dialogs: UnreadDialog[]): number {
  return (dialogs || []).reduce((acc, dialog) => acc + unreadCountFromDialogRecord(dialog), 0);
}

const UnreadMessagesBadge = ({ count }: { count: number }) => {
  return <span className={styles.notifications}>{count > 99 ? '99+' : count}</span>;
};

const useOperatorPermissions = () => {
  const [hasChatPermissions, setHasChatPermissions] = useState(false);

  useEffect(() => {
    const unsubscribe = appStore.subscribe(() => {
      const permissions = appStore.getState().permissions || [];

      const hasPermissions = permissions.some((permission: string) =>
        permission.includes('PERMISSION_OPERATOR_CHATS'),
      );

      setHasChatPermissions(hasPermissions);
    });

    const initialPermissions = appStore.getState().permissions || [];
    const hasInitialPermissions = initialPermissions.some((permission: string) =>
      permission.includes('PERMISSION_OPERATOR_CHATS'),
    );
    setHasChatPermissions(hasInitialPermissions);

    return unsubscribe;
  }, []);

  return hasChatPermissions;
};

const ChatToggleButton = ({
  isOperatorChatPopupWindow = false,
}: {
  isOperatorChatPopupWindow?: boolean;
}) => {
  const { t } = useTranslation();
  const { isChatOpen, setIsChatOpen, sessions, closeSession, createNewSession, activeSessionId } =
    useChat();
  const {
    calculateTotalUnread,
    dialogsUnreadCounts,
    mergeDialogUnreadFromApi,
    restrictUnreadCountsToDialogIds,
  } = useSocket();
  const isDesktopShell = typeof window !== 'undefined' && Boolean(window.alcolockDesktop);
  const [isDesktopPopupOpen, setIsDesktopPopupOpen] = useState(() =>
    isDesktopShell ? readMainChatFooterSuppressedByPopup() : false,
  );
  const [restBadgeTotal, setRestBadgeTotal] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;
    let timer: number | undefined;
    let lastBranchId: string | null = null;

    const applyList = (list: UnreadDialog[]) => {
      setRestBadgeTotal(sumUnreadDialogCounts(list));
      const ids = list.map((d) => Number(d.id)).filter((id) => id > 0);
      if (ids.length > 0) {
        restrictUnreadCountsToDialogIds(ids);
        list.forEach((dialog) => {
          mergeDialogUnreadFromApi(Number(dialog.id), unreadCountFromDialogRecord(dialog));
        });
      }
    };

    const loadClosedChatBadge = (attempt = 0) => {
      if (cancelled) return;
      if (!getBearerToken()) return;
      const branchId = getCurrentOperatorBranchId();
      if (!branchId) return;

      void (async () => {
        try {
          const response = await DialogsApi.getUnreadDialogs();
          if (cancelled) return;
          const list = (response?.data?.content ?? []) as UnreadDialog[];
          applyList(list);
          const sum = sumUnreadDialogCounts(list);
          if (sum === 0 && attempt < 4) {
            timer = window.setTimeout(() => loadClosedChatBadge(attempt + 1), 500);
          }
        } catch {
          if (!cancelled && attempt < 4) {
            timer = window.setTimeout(() => loadClosedChatBadge(attempt + 1), 500);
          }
        }
      })();
    };

    const onStore = () => {
      if (!getBearerToken()) return;
      const branchId = getCurrentOperatorBranchId();
      if (!branchId || branchId === lastBranchId) return;
      lastBranchId = branchId;
      loadClosedChatBadge(0);
    };

    onStore();
    const unsubscribe = appStore.subscribe(onStore);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      unsubscribe();
    };
  }, [isChatOpen, mergeDialogUnreadFromApi, restrictUnreadCountsToDialogIds]);
  const iconUnreadTotalBase = calculateTotalUnread();
  const electronSessionsUnreadSum =
    isElectronChatShell() && sessions.length > 0
      ? sessions.reduce((acc, s) => {
          if (!sessionBelongsToCurrentOperatorBranch(s)) return acc;
          return acc + effectiveMinimizedSessionUnread(s, dialogsUnreadCounts);
        }, 0)
      : null;
  // Редкий кейс сразу после жёсткой перезагрузки: общий бейдж может кратковременно быть 0,
  // пока WS-карта/агрегат не синхронизировались, но в сессии уже есть непрочитанные по ленте.
  // Не даём показывать 0, если хоть где-то в сессиях вычисляется unread>0.
  const maxSessionUnreadFallback = sessions.reduce((acc: number, s: any) => {
    if (!sessionBelongsToCurrentOperatorBranch(s)) return acc;
    return Math.max(acc, effectiveMinimizedSessionUnread(s, dialogsUnreadCounts));
  }, 0);
  // Бейдж иконки — только диалоги текущего филиала (allowlist после REST), без агрегата всех филиалов.
  const iconUnreadTotal =
    electronSessionsUnreadSum != null
      ? Math.max(electronSessionsUnreadSum, iconUnreadTotalBase)
      : Math.max(iconUnreadTotalBase, maxSessionUnreadFallback, isChatOpen ? 0 : restBadgeTotal);

  const handleToggle = () => {
    if (isOperatorChatPopupWindow) {
      if (isChatOpen) {
        closeOperatorChatPopupAndRestoreMain();
        return;
      }
      setIsChatOpen(true);
      if (sessions.length === 0) {
        createNewSession();
      }
    } else if (isDesktopShell) {
      if (isDesktopPopupOpen) {
        void (async () => {
          const desktop = window.alcolockDesktop;
          if (!desktop) return;
          // Popup уже открыт: если не в фокусе (ушёл «под» main) — только поднять;
          // если уже в фокусе — закрыть (как раньше).
          const focused = await desktop.isOperatorChatPopupFocused?.();
          if (focused) {
            await desktop.closeOperatorChatPopup();
            setIsDesktopPopupOpen(false);
            return;
          }
          const raised = await desktop.focusOperatorChatPopup?.();
          if (!raised) {
            // Окно пропало, а heartbeat ещё жив — откроем заново.
            persistMainToOperatorPopupHandoff({
              isChatOpen: true,
              sessions,
              activeSessionId,
              aggregateUnread: calculateTotalUnread(),
              dialogsUnreadCounts,
            });
            openOperatorChatPopup();
            setIsDesktopPopupOpen(true);
          }
        })();
        return;
      }
      persistMainToOperatorPopupHandoff({
        isChatOpen: true,
        sessions,
        activeSessionId,
        aggregateUnread: calculateTotalUnread(),
        dialogsUnreadCounts,
      });
      openOperatorChatPopup();
      setIsDesktopPopupOpen(true);
    } else if (isChatOpen) {
      sessions.forEach((session) => {
        closeSession(session.id);
      });
      setIsChatOpen(false);
    } else {
      setIsChatOpen(true);
      createNewSession();
    }
  };

  const tooltipTitle = isOperatorChatPopupWindow
    ? isChatOpen
      ? 'Закрыть диалоговое окно'
      : 'Открыть диалоговое окно'
    : isDesktopShell
      ? isDesktopPopupOpen
        ? 'Закрыть диалоговое окно'
        : 'Открыть диалоговое окно'
      : isChatOpen
        ? t('chat.toggleTooltip', { count: iconUnreadTotal })
        : t('chat.openChatWindow');

  useEffect(() => {
    if (!isDesktopShell) return;

    const syncDesktopPopupState = () => {
      setIsDesktopPopupOpen(readMainChatFooterSuppressedByPopup());
    };

    const onStorage = (event: StorageEvent) => {
      if (
        event.key === CHAT_POPUP_ACTIVE_STORAGE_KEY ||
        event.key === CHAT_DESKTOP_POPUP_CLOSED_EVENT_KEY
      ) {
        syncDesktopPopupState();
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(CHAT_DESKTOP_POPUP_CLOSED_EVENT_KEY, syncDesktopPopupState);
    const timer = window.setInterval(syncDesktopPopupState, CHAT_POPUP_HEARTBEAT_MS);
    syncDesktopPopupState();

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHAT_DESKTOP_POPUP_CLOSED_EVENT_KEY, syncDesktopPopupState);
      window.clearInterval(timer);
    };
  }, [isDesktopShell]);

  return (
    <Tooltip title={tooltipTitle} placement="left">
      <div className={styles.toggleButtonWrapper}>
        <IconButton
          className={styles.toggleButton}
          onClick={handleToggle}
          color="primary"
          size="large">
          {(isOperatorChatPopupWindow && isChatOpen) ||
          (isDesktopShell && isDesktopPopupOpen) ||
          (isChatOpen && !isDesktopShell && !isOperatorChatPopupWindow) ? (
            <CloseIcon />
          ) : (
            <ChatIcon />
          )}
        </IconButton>
        <UnreadMessagesBadge count={iconUnreadTotal} />
      </div>
    </Tooltip>
  );
};

const NewChatButton = () => {
  const { t } = useTranslation();
  const { createNewSession, sessions, toggleSessionMinimize, setActiveSessionId } = useChat();

  const handleNewChat = () => {
    sessions.forEach((session) => {
      if (!session.isMinimized) {
        toggleSessionMinimize(session.id);
      }
    });

    const newSessionId = createNewSession();
    setActiveSessionId(newSessionId);
  };

  // Показываем кнопку только если есть сессии с выбранным пользователем
  // Если все сессии без пользователя (новый чат) - кнопка не нужна
  const hasSessionWithUser = sessions.some(
    (s) => (s.selectedUsers?.length ?? 0) > 0 || s.selectedUserName?.trim()
  );
  
  if (sessions.length === 0 || !hasSessionWithUser) return null;

  return (
    <Tooltip title={t('chat.openNewChat')} placement="left">
      <div className={styles.newChatButtonWrapper}>
        <IconButton
          className={styles.newChatButton}
          onClick={handleNewChat}
          color="primary"
          size="large">
          <AddIcon />
        </IconButton>
      </div>
    </Tooltip>
  );
};

const ChatContainer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isOperatorChatPopupWindow = location.pathname === RoutePaths.operatorChatPopup;
  const isBrowserDetachedChatPopup = isOperatorChatPopupWindow && isBrowserWebChatShell();
  const isDesktopShell = typeof window !== 'undefined' && Boolean(window.alcolockDesktop);
  /** В Electron чат живёт только в откреплённом окне; в главном — только FAB-кнопки. */
  const isDesktopMainChatHost = isDesktopShell && !isOperatorChatPopupWindow;
  const {
    isChatOpen,
    sessions,
    activeSessionId,
    setActiveSessionId,
    closeSession,
    createNewSession,
    expandSession,
    forceRefreshSessionMessages,
    toggleSessionMinimize,
    setIsChatOpen,
    openUnreadDialog,
    hasSessionWithUser,
    forceLoadUnreadDialogs,
  } = useChat();

  const sessionsForBranchClearRef = useRef(sessions);
  const closeSessionForBranchClearRef = useRef(closeSession);
  sessionsForBranchClearRef.current = sessions;
  closeSessionForBranchClearRef.current = closeSession;

  const {
    lastMessage,
    dialogsUnreadCounts,
    unreadCount: socketUnreadTotal,
    calculateTotalUnread,
  } = useSocket();
  const [isVisible, setIsVisible] = useState(true);
  const [justExpandedSessionId, setJustExpandedSessionId] = useState<string | null>(null);
  const hasChatPermissions = useOperatorPermissions();

  /** Electron main: держим handoff в LS актуальным до открытия popup. */
  useEffect(() => {
    if (!isDesktopMainChatHost) return;
    if (sessions.length === 0) return;
    persistMainToOperatorPopupHandoff({
      isChatOpen,
      sessions,
      activeSessionId,
      aggregateUnread: calculateTotalUnread(),
      dialogsUnreadCounts,
    });
  }, [
    isDesktopMainChatHost,
    sessions,
    isChatOpen,
    activeSessionId,
    dialogsUnreadCounts,
    calculateTotalUnread,
  ]);

  // Агрегат и карта по диалогам ведёт только SocketContext (кадры WS). Ранее здесь
  // вызывался setUnreadCount по любому lastMessage с countUnMessages — в том числе
  // DIALOGS_UPDATE с 0 — это обнуляло бейдж и ломало mergeDialogUnreadFromApi.

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.shiftKey && event.ctrlKey) {
        const key = event.key.toLowerCase();
        if (key === 'd' || key === 'в') {
          event.preventDefault();
          setIsVisible((prev) => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (sessions.length === 0 && isChatOpen) {
      if (isOperatorChatPopupWindow) {
        createNewSession();
        return;
      }
      let skipEmptyClose = false;
      try {
        skipEmptyClose =
          sessionStorage.getItem(CHAT_MAIN_RESTORE_SKIP_EMPTY_CLOSE_ONCE_SESSION_KEY) === '1';
        if (skipEmptyClose) {
          sessionStorage.removeItem(CHAT_MAIN_RESTORE_SKIP_EMPTY_CLOSE_ONCE_SESSION_KEY);
        } else if (
          localStorage.getItem(CHAT_MAIN_RESTORE_SKIP_EMPTY_CLOSE_ONCE_LOCAL_KEY) === '1'
        ) {
          skipEmptyClose = true;
          localStorage.removeItem(CHAT_MAIN_RESTORE_SKIP_EMPTY_CLOSE_ONCE_LOCAL_KEY);
        }
      } catch {
        /* ignore */
      }
      if (skipEmptyClose) {
        // Electron main не восстанавливает чат UI — skip REST.
        if (isDesktopShell && !isOperatorChatPopupWindow) {
          setIsChatOpen(false);
          return;
        }
        const newSessionId = createNewSession();
        if (newSessionId) {
          void forceLoadUnreadDialogs(newSessionId);
        }
        return;
      }
      setIsChatOpen(false);
      return;
    }
    if (sessions.length > 0 && !isChatOpen) {
      setIsChatOpen(true);
    }
  }, [
    sessions,
    isChatOpen,
    setIsChatOpen,
    createNewSession,
    forceLoadUnreadDialogs,
    isOperatorChatPopupWindow,
  ]);

  useEffect(() => {
    if (isOperatorChatPopupWindow || !window.alcolockDesktop) return;

    const closeMainDesktopChatState = () => {
      sessionsForBranchClearRef.current.forEach((session) => {
        closeSessionForBranchClearRef.current(session.id);
      });
      setIsChatOpen(false);
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === CHAT_DESKTOP_POPUP_CLOSED_EVENT_KEY) {
        closeMainDesktopChatState();
      }
    };

    const onCustomEvent = () => closeMainDesktopChatState();

    window.addEventListener('storage', onStorage);
    window.addEventListener(CHAT_DESKTOP_POPUP_CLOSED_EVENT_KEY, onCustomEvent);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHAT_DESKTOP_POPUP_CLOSED_EVENT_KEY, onCustomEvent);
    };
  }, [isOperatorChatPopupWindow, setIsChatOpen]);

  const operatorChatSessionRestoreFingerprint = useMemo(() => {
    if (!isOperatorChatPopupWindow) return '';
    try {
      return JSON.stringify(
        sessions.map((s) => ({
          id: s.id,
          min: s.isMinimized,
          sd: s.selectedDialog?.id ?? null,
          ad: s.assignedDialogId ?? null,
          su: s.selectedUsers,
        })),
      );
    } catch {
      return '';
    }
  }, [isOperatorChatPopupWindow, sessions]);

  // Пока живёт окно popup — пишем открытость панели и снимок сессий (в т.ч. закрытие вкладки по X).
  useEffect(() => {
    if (!isOperatorChatPopupWindow) return;
    persistMainRestoreFromPopupState({ isChatOpen, sessions, activeSessionId });
  }, [
    isOperatorChatPopupWindow,
    isChatOpen,
    activeSessionId,
    operatorChatSessionRestoreFingerprint,
  ]);

  // Popup (web и Electron): список диалогов для превью — как в браузерной версии.
  useEffect(() => {
    if (!isOperatorChatPopupWindow) return;
    if (isDesktopMainChatHost) return;
    if (sessions.length === 0) return;

    const loadForSessions = () => {
      if (!getBearerToken()) return;
      sessions.forEach((session) => {
        if (session.unreadDialogs.length === 0 && !session.isLoadingUnreadDialogs) {
          void forceLoadUnreadDialogs(session.id);
        }
      });
    };

    loadForSessions();

    if (!isDesktopShell) return;

    window.addEventListener(DESKTOP_AUTH_READY_EVENT, loadForSessions);
    window.addEventListener(DESKTOP_BRANCH_READY_EVENT, loadForSessions);
    window.addEventListener(ELECTRON_POPUP_REQUEST_UNREAD_REST_EVENT, loadForSessions);
    return () => {
      window.removeEventListener(DESKTOP_AUTH_READY_EVENT, loadForSessions);
      window.removeEventListener(DESKTOP_BRANCH_READY_EVENT, loadForSessions);
      window.removeEventListener(ELECTRON_POPUP_REQUEST_UNREAD_REST_EVENT, loadForSessions);
    };
  }, [
    isOperatorChatPopupWindow,
    isDesktopMainChatHost,
    isDesktopShell,
    sessions,
    forceLoadUnreadDialogs,
  ]);

  /** Только при смене филиала. Подписка монтируется один раз: иначе [sessions, closeSession] пересоздаёт
   * closeSession на каждом обновлении сессий → бесконечный resubscribe и гонки с «дёрганьем» после handoff. */
  const branchChatSubInitializedRef = useRef(false);
  const lastBranchIdForChatClearRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = appStore.subscribe(() => {
      const nextId = normalizeBranchIdForChatClear(appStore.getState().selectedBranchState?.id);
      if (!branchChatSubInitializedRef.current) {
        branchChatSubInitializedRef.current = true;
        lastBranchIdForChatClearRef.current = nextId;
        return;
      }
      if (lastBranchIdForChatClearRef.current === nextId) return;
      const prev = lastBranchIdForChatClearRef.current;
      lastBranchIdForChatClearRef.current = nextId;
      /* Первое появление филиала в сторе после null — не считаем сменой (иначе сразу после
       * handoff второй колбэк zustand обнуляет сессии). Реальная смена: оба prev и next не null. */
      if (prev === null && nextId !== null) {
        return;
      }
      sessionsForBranchClearRef.current.forEach((session) => {
        closeSessionForBranchClearRef.current(session.id);
      });
    });

    return () => unsubscribe();
  }, []);

  const handleToggleSessionMinimize = (sessionId: string) => {
    toggleSessionMinimize(sessionId);
    setActiveSessionId(null);
  };

  const handleExpandSession = useCallback(
    (sessionId: string) => {
      const snap = sessions.map((s) => ({
        id: s.id,
        min: s.isMinimized,
        user: s.selectedUsers?.[0],
        dialog:
          s.selectedDialog?.id != null
            ? String(s.selectedDialog.id)
            : s.assignedDialogId != null
              ? String(s.assignedDialogId)
              : null,
        unread: s.unreadCount,
      }));
      operatorUnreadDebug(
        'Раскрытие сессии из превью (включится скролл к первому непрочитанному при необходимости)',
        {
          целеваяСессия: sessionId,
          сессии: snap,
          развёрнуты: sessions.filter((s) => !s.isMinimized).map((s) => s.id),
          свёрнуты: sessions.filter((s) => s.isMinimized).map((s) => s.id),
        },
      );
      setJustExpandedSessionId(sessionId);
      expandSession(sessionId);
      // После раскрытия из превью синхронизируем сессию с актуальными данными диалога
      // (в т.ч. status/lastOperator), чтобы избежать отображения устаревшего блокирующего оператора.
      setTimeout(() => {
        void forceRefreshSessionMessages(sessionId);
      }, 0);
    },
    [expandSession, forceRefreshSessionMessages, sessions],
  );

  const handleScrollToBottomDone = useCallback(() => {
    setJustExpandedSessionId(null);
  }, []);

  const handleOperatorChatWindowButtonClick = useCallback(() => {
    if (isOperatorChatPopupWindow) {
      persistMainRestoreFromPopupState({ isChatOpen, sessions, activeSessionId });
      closeOperatorChatPopupAndRestoreMain();
    } else {
      persistMainToOperatorPopupHandoff({
        isChatOpen,
        sessions,
        activeSessionId,
        aggregateUnread: calculateTotalUnread(),
        dialogsUnreadCounts,
      });
      openOperatorChatPopup();
    }
  }, [isOperatorChatPopupWindow, isChatOpen, sessions, activeSessionId, calculateTotalUnread, dialogsUnreadCounts]);

  const operatorChatWindowButtonLabel = useMemo(() => {
    if (isOperatorChatPopupWindow) {
      return t('chat.returnToSingleWindow');
    }
    // Для web-версии: если нет развёрнутых сессий - "открыть диалоговое окно", иначе - "вернуться"
    if (sessions.filter((s) => !s.isMinimized).length === 0) {
      return t('chat.openChatWindow');
    }
    return t('chat.openInSeparateWindow');
  }, [isOperatorChatPopupWindow, sessions, t]);

  const handleCloseAllChats = useCallback(() => {
    sessions.forEach((session) => {
      closeSession(session.id);
    });
    setIsChatOpen(false);
  }, [sessions, closeSession, setIsChatOpen]);

  const isCompactMinimizedUi = useMediaQuery(CHAT_COMPACT_MINIMIZED_QUERY);
  const [minimizedListOpen, setMinimizedListOpen] = useState(false);

  const expandedSessionCount = useMemo(
    () => sessions.filter((s) => !s.isMinimized).length,
    [sessions],
  );
  // В попапе после shrink-to-fit окно часто <1024px — без исключения dock пропадал бы из‑за compact.
  // Пустой dock при 0 развёрнутых сессиях нельзя: useOperatorChatPopupWindowFrame схлопывает окно под колонку FAB.
  const allowDesktopPanelResize =
    !isDesktopMainChatHost &&
    expandedSessionCount > 0 &&
    (!isCompactMinimizedUi || isOperatorChatPopupWindow);

  const desiredPanelSizeRef = useRef<ChatFooterPanelSize>({ ...DEFAULT_CHAT_PANEL });
  const [panelSize, setPanelSize] = useState<ChatFooterPanelSize>(() => {
    const desired = readDesiredPanelSize(isOperatorChatPopupWindow);
    desiredPanelSizeRef.current = desired;
    return desired;
  });

  const chatUiScale = useMemo(() => {
    if (!allowDesktopPanelResize) return 1;
    const sw = panelSize.w / DEFAULT_CHAT_PANEL.w;
    const sh = panelSize.h / DEFAULT_CHAT_PANEL.h;
    const raw = Math.min(sw, sh);
    return Math.min(Math.max(raw, 0.78), 1.38);
  }, [allowDesktopPanelResize, panelSize.h, panelSize.w]);

  type CompactMinimizedEntry =
    | {
        kind: 'session';
        key: string;
        sessionId: string;
        title: string;
        subtitle?: string;
        unread: number;
      }
    | {
        kind: 'unread';
        key: string;
        sessionId: string;
        dialog: UnreadDialog;
        title: string;
        subtitle?: string;
        unread: number;
      };

  const dedupedUnreadPreviewRows = useMemo(
    () => collectDedupedUnreadDialogsForPreview(sessions, hasSessionWithUser),
    [sessions, hasSessionWithUser],
  );

  const expandedSessions = useMemo(() => sessions.filter((s) => !s.isMinimized), [sessions]);
  const desktopPopupPrimaryExpandedSessionId = useMemo(() => {
    if (!isOperatorChatPopupWindow || !isDesktopShell) return null;
    if (activeSessionId && expandedSessions.some((session) => session.id === activeSessionId)) {
      return activeSessionId;
    }
    return expandedSessions[0]?.id ?? null;
  }, [activeSessionId, expandedSessions, isDesktopShell, isOperatorChatPopupWindow]);
  const expandedSessionsForDock = useMemo(() => {
    if (!desktopPopupPrimaryExpandedSessionId) return expandedSessions;
    return expandedSessions.filter((session) => session.id === desktopPopupPrimaryExpandedSessionId);
  }, [desktopPopupPrimaryExpandedSessionId, expandedSessions]);
  const desktopPopupExtraExpandedPreviewSessions = useMemo(() => {
    if (!desktopPopupPrimaryExpandedSessionId) return [];
    return expandedSessions.filter((session) => session.id !== desktopPopupPrimaryExpandedSessionId);
  }, [desktopPopupPrimaryExpandedSessionId, expandedSessions]);

  const minimizedSessions = useMemo(
    () =>
      [
        ...desktopPopupExtraExpandedPreviewSessions.filter(shouldShowSessionInSidePreview),
        ...sessions.filter(
          (session) => session.isMinimized && shouldShowSessionInSidePreview(session),
        ),
      ],
    [desktopPopupExtraExpandedPreviewSessions, sessions],
  );

  const [isChatLayoutPinned, setIsChatLayoutPinned] = useState(() =>
    readChatLayoutPinned(isOperatorChatPopupWindow),
  );
  const popupDockDefaults = defaultDockMargins(isOperatorChatPopupWindow);
  const [dockRightPx, setDockRightPx] = useState(
    () => readInitialDockMargins(isOperatorChatPopupWindow).r,
  );
  const [dockBottomPx, setDockBottomPx] = useState(
    () => readInitialDockMargins(isOperatorChatPopupWindow).b,
  );
  const [isDockDragging, setIsDockDragging] = useState(false);

  const dockPosRef = useRef({ r: popupDockDefaults.r, b: popupDockDefaults.b });
  /** Чтобы при первом mount с 0 сессий не сбрасывать dock; сброс только при переходе N>0 → 0 (закрыли весь чат). */
  const prevSessionsLenForDockRef = useRef<number | null>(null);
  /** Только основная вкладка: свернули последнюю панель — сброс размеров/позиции dock в LS. В попапе не трогаем. */
  const prevAllowDesktopPanelResizeRef = useRef<boolean | null>(null);
  const dockDimensionsRef = useRef({ dockW: DEFAULT_CHAT_PANEL.w, dockH: DEFAULT_CHAT_PANEL.h });

  const persistChatLayoutGeometry = useCallback(() => {
    try {
      const k = chatPanelDockStorageKeys(isOperatorChatPopupWindow);
      const { r, b } = dockPosRef.current;
      if (!isBrowserDetachedChatPopup) {
        localStorage.setItem(k.panelW, String(panelSize.w));
        localStorage.setItem(k.panelH, String(panelSize.h));
      }
      localStorage.setItem(k.dockR, String(r));
      localStorage.setItem(k.dockB, String(b));
    } catch {
      /* ignore */
    }
  }, [isBrowserDetachedChatPopup, isOperatorChatPopupWindow, panelSize.h, panelSize.w]);

  const applyPinnedLayoutFromStorage = useCallback(() => {
    if (isBrowserDetachedChatPopup) {
      const next = { w: POPUP_DOCK_PANEL_W_PX, h: POPUP_DOCK_PANEL_H_PX };
      desiredPanelSizeRef.current = next;
      setPanelSize(next);
      return;
    }
    if (!readChatLayoutPinned(isOperatorChatPopupWindow)) return;
    const saved = readSavedPanelSize(isOperatorChatPopupWindow);
    const dock = readSavedDockMargins(isOperatorChatPopupWindow);
    if (saved) {
      const next = {
        w: Math.max(MIN_CHAT_PANEL.w, saved.w),
        h: Math.max(MIN_CHAT_PANEL.h, saved.h),
      };
      desiredPanelSizeRef.current = next;
      setPanelSize(next);
    }
    if (dock) {
      const { dockW, dockH } = dockDimensionsRef.current;
      const clamped = clampDockMargins(dock.r, dock.b, dockW, dockH);
      setDockRightPx(clamped.r);
      setDockBottomPx(clamped.b);
      dockPosRef.current = { r: clamped.r, b: clamped.b };
      try {
        const k = chatPanelDockStorageKeys(isOperatorChatPopupWindow);
        localStorage.setItem(k.dockR, String(clamped.r));
        localStorage.setItem(k.dockB, String(clamped.b));
      } catch {
        /* ignore */
      }
    }
  }, [isBrowserDetachedChatPopup, isOperatorChatPopupWindow]);

  useLayoutEffect(() => {
    if (!isBrowserDetachedChatPopup) return;
    const next = { w: POPUP_DOCK_PANEL_W_PX, h: POPUP_DOCK_PANEL_H_PX };
    desiredPanelSizeRef.current = next;
    setPanelSize(next);
  }, [isBrowserDetachedChatPopup]);

  const handleToggleChatLayoutPin = useCallback(() => {
    const nextPinned = !isChatLayoutPinned;
    if (nextPinned) {
      dockPosRef.current = { r: dockRightPx, b: dockBottomPx };
      try {
        const k = chatPanelDockStorageKeys(isOperatorChatPopupWindow);
        localStorage.setItem(k.panelW, String(panelSize.w));
        localStorage.setItem(k.panelH, String(panelSize.h));
        localStorage.setItem(k.dockR, String(dockRightPx));
        localStorage.setItem(k.dockB, String(dockBottomPx));
      } catch {
        /* ignore */
      }
      if (isOperatorChatPopupWindow) {
        try {
          writeOperatorChatPopupFrameLock({
            outerW: window.outerWidth,
            outerH: window.outerHeight,
            left: window.screenX,
            top: window.screenY,
          });
        } catch {
          /* ignore */
        }
      }
    }
    writeChatLayoutPinned(isOperatorChatPopupWindow, nextPinned);
    setIsChatLayoutPinned(nextPinned);
  }, [
    dockBottomPx,
    dockRightPx,
    isChatLayoutPinned,
    isOperatorChatPopupWindow,
    panelSize.h,
    panelSize.w,
  ]);

  useLayoutEffect(() => {
    if (!isChatLayoutPinned || !allowDesktopPanelResize) return;
    applyPinnedLayoutFromStorage();
  }, [isChatLayoutPinned, allowDesktopPanelResize, applyPinnedLayoutFromStorage]);

  useEffect(() => {
    if (!isChatOpen || !readChatLayoutPinned(isOperatorChatPopupWindow)) return;
    applyPinnedLayoutFromStorage();
  }, [isChatOpen, isOperatorChatPopupWindow, applyPinnedLayoutFromStorage]);

  useEffect(() => {
    if (!isOperatorChatPopupWindow) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== CHAT_POPUP_LAYOUT_PINNED && !e.key?.startsWith('alcolock_operator_chat_popup_')) {
        return;
      }
      const pinned = readChatLayoutPinned(true);
      setIsChatLayoutPinned(pinned);
      if (pinned) {
        applyPinnedLayoutFromStorage();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isOperatorChatPopupWindow, applyPinnedLayoutFromStorage]);

  /** Сброс геометрии чата только в основном окне (не в operator-chat-popup). */
  const resetMainWindowChatLayoutToDefaults = useCallback(() => {
    if (isChatLayoutPinned) return;
    desiredPanelSizeRef.current = { ...DEFAULT_CHAT_PANEL };
    setPanelSize({ ...DEFAULT_CHAT_PANEL });
    setDockRightPx(CHAT_FOOTER_RIGHT);
    setDockBottomPx(CHAT_FOOTER_BOTTOM);
    dockPosRef.current = { r: CHAT_FOOTER_RIGHT, b: CHAT_FOOTER_BOTTOM };
    try {
      const k = chatPanelDockStorageKeys(false);
      localStorage.removeItem(k.panelW);
      localStorage.removeItem(k.panelH);
      localStorage.removeItem(k.dockR);
      localStorage.removeItem(k.dockB);
    } catch {
      /* ignore */
    }
  }, [isChatLayoutPinned]);

  const dockDimensions = useMemo(() => {
    if (!allowDesktopPanelResize) {
      return { dockW: DEFAULT_CHAT_PANEL.w, dockH: DEFAULT_CHAT_PANEL.h };
    }
    const previewCount = minimizedSessions.length + dedupedUnreadPreviewRows.length;
    const pw = panelSize.w;
    const ph = panelSize.h;
    const nExp = expandedSessionsForDock.length;
    const previewColW = previewCount > 0 ? 260 : 0;
    const previewStackH =
      previewCount > 0 ? previewCount * DOCK_PREVIEW_ROW_APPROX_PX + (previewCount - 1) * 8 : 0;
    const panelsStackH = nExp * ph + Math.max(0, nExp - 1) * 8;
    const dockH = Math.max(previewStackH, panelsStackH, DOCK_FAB_STACK_H_PX);
    const dockW =
      DOCK_FAB_COLUMN_W_PX +
      2 * MINIMIZED_PREVIEW_GAP_PX +
      pw +
      (previewCount > 0 ? previewColW : 0);
    return { dockW, dockH };
  }, [
    allowDesktopPanelResize,
    dedupedUnreadPreviewRows.length,
    expandedSessionsForDock.length,
    minimizedSessions.length,
    panelSize.h,
    panelSize.w,
  ]);

  dockDimensionsRef.current = dockDimensions;

  dockPosRef.current = { r: dockRightPx, b: dockBottomPx };

  useEffect(() => {
    const prev = prevSessionsLenForDockRef.current;
    prevSessionsLenForDockRef.current = sessions.length;
    if (prev === null) {
      return;
    }
    if (prev > 0 && sessions.length === 0 && !isChatLayoutPinned) {
      if (isOperatorChatPopupWindow) {
        const d = defaultDockMargins(true);
        setDockRightPx(d.r);
        setDockBottomPx(d.b);
        dockPosRef.current = { r: d.r, b: d.b };
        try {
          const k = chatPanelDockStorageKeys(true);
          localStorage.removeItem(k.dockR);
          localStorage.removeItem(k.dockB);
        } catch {
          /* ignore */
        }
      } else {
        resetMainWindowChatLayoutToDefaults();
      }
    }
  }, [
    sessions.length,
    isOperatorChatPopupWindow,
    isChatLayoutPinned,
    resetMainWindowChatLayoutToDefaults,
  ]);

  useEffect(() => {
    const prev = prevAllowDesktopPanelResizeRef.current;
    const cur = allowDesktopPanelResize;
    prevAllowDesktopPanelResizeRef.current = cur;
    if (prev !== true || cur !== false) return;
    if (isOperatorChatPopupWindow) return;
    if (isChatLayoutPinned) return;
    resetMainWindowChatLayoutToDefaults();
  }, [
    allowDesktopPanelResize,
    isOperatorChatPopupWindow,
    isChatLayoutPinned,
    resetMainWindowChatLayoutToDefaults,
  ]);

  const handleDockDragPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!allowDesktopPanelResize || isChatLayoutPinned) return;
      if (e.button !== 0) return;
      e.preventDefault();
      const pointerId = e.pointerId;
      const startX = e.clientX;
      const startY = e.clientY;
      const startR = dockPosRef.current.r;
      const startB = dockPosRef.current.b;
      setIsDockDragging(true);

      const onMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        ev.preventDefault();
        const { dockW, dockH } = dockDimensionsRef.current;
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        const { r, b } = clampDockMargins(startR - dx, startB - dy, dockW, dockH);
        dockPosRef.current = { r, b };
        setDockRightPx(r);
        setDockBottomPx(b);
      };

      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        ev.preventDefault();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        setIsDockDragging(false);
        try {
          const { r, b } = dockPosRef.current;
          const k = chatPanelDockStorageKeys(isOperatorChatPopupWindow);
          localStorage.setItem(k.dockR, String(r));
          localStorage.setItem(k.dockB, String(b));
        } catch {
          /* ignore */
        }
      };

      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp, { passive: false });
      window.addEventListener('pointercancel', onUp, { passive: false });
    },
    [allowDesktopPanelResize, isChatLayoutPinned, isOperatorChatPopupWindow],
  );

  const dockPreviewCount = minimizedSessions.length + dedupedUnreadPreviewRows.length;

  const resolvePanelSize = useCallback(
    (s: ChatFooterPanelSize) => {
      if (isBrowserDetachedChatPopup) {
        return { w: POPUP_DOCK_PANEL_W_PX, h: POPUP_DOCK_PANEL_H_PX };
      }
      if (!allowDesktopPanelResize) return clampPanelSize(s);
      if (isOperatorChatPopupWindow) {
        return {
          w: Math.max(MIN_CHAT_PANEL.w, s.w),
          h: Math.max(MIN_CHAT_PANEL.h, s.h),
        };
      }
      return clampPanelSizeForFloatingDock(s, {
        previewCount: dockPreviewCount,
        expandedCount: expandedSessionsForDock.length,
        dockRightPx,
        dockBottomPx,
      });
    },
    [
      allowDesktopPanelResize,
      isBrowserDetachedChatPopup,
      isOperatorChatPopupWindow,
      dockPreviewCount,
      expandedSessionsForDock.length,
      dockRightPx,
      dockBottomPx,
    ],
  );

  const getMaxPanelSize = useCallback(() => {
    if (isOperatorChatPopupWindow) {
      return {
        w: Math.max(panelSize.w, DEFAULT_CHAT_PANEL.w),
        h: Math.max(panelSize.h, DEFAULT_CHAT_PANEL.h),
      };
    }
    return getMaxPanelSizeForFloatingDock({
      previewCount: dockPreviewCount,
      expandedCount: expandedSessionsForDock.length,
      dockRightPx,
      dockBottomPx,
    });
  }, [
    isOperatorChatPopupWindow,
    panelSize.w,
    panelSize.h,
    dockPreviewCount,
    expandedSessionsForDock.length,
    dockRightPx,
    dockBottomPx,
  ]);

  const handlePanelSizeCommit = useCallback(
    (next: ChatFooterPanelSize) => {
      if (isBrowserDetachedChatPopup || isChatLayoutPinned) return;
      const clamped = resolvePanelSize(next);
      desiredPanelSizeRef.current = clamped;
      setPanelSize(clamped);
      try {
        const k = chatPanelDockStorageKeys(isOperatorChatPopupWindow);
        localStorage.setItem(k.panelW, String(clamped.w));
        localStorage.setItem(k.panelH, String(clamped.h));
      } catch {
        /* ignore */
      }
    },
    [resolvePanelSize, isOperatorChatPopupWindow, isChatLayoutPinned, isBrowserDetachedChatPopup],
  );

  useLayoutEffect(() => {
    if (!allowDesktopPanelResize || isChatLayoutPinned || isOperatorChatPopupWindow) return;
    setPanelSize((prev) => {
      const next = resolvePanelSize(desiredPanelSizeRef.current);
      if (prev.w === next.w && prev.h === next.h) return prev;
      return next;
    });
  }, [allowDesktopPanelResize, isChatLayoutPinned, isOperatorChatPopupWindow, resolvePanelSize]);

  const reclampFloatingDockLayout = useCallback(() => {
    if (!allowDesktopPanelResize) return;
    const { dockW, dockH } = dockDimensionsRef.current;
    const c = clampDockMargins(dockPosRef.current.r, dockPosRef.current.b, dockW, dockH);
    if (c.r !== dockPosRef.current.r || c.b !== dockPosRef.current.b) {
      dockPosRef.current = c;
      setDockRightPx(c.r);
      setDockBottomPx(c.b);
      if (isChatLayoutPinned) {
        try {
          const k = chatPanelDockStorageKeys(isOperatorChatPopupWindow);
          localStorage.setItem(k.dockR, String(c.r));
          localStorage.setItem(k.dockB, String(c.b));
        } catch {
          /* ignore */
        }
      }
    }
    if (!isChatLayoutPinned && !isOperatorChatPopupWindow) {
      setPanelSize((prev) => {
        const next = resolvePanelSize(desiredPanelSizeRef.current);
        if (prev.w === next.w && prev.h === next.h) return prev;
        return next;
      });
    }
  }, [
    allowDesktopPanelResize,
    isChatLayoutPinned,
    isOperatorChatPopupWindow,
    resolvePanelSize,
  ]);

  useEffect(() => {
    if (!allowDesktopPanelResize) return;
    const onViewportChange = () => {
      reclampFloatingDockLayout();
    };
    window.addEventListener('resize', onViewportChange);
    window.visualViewport?.addEventListener('resize', onViewportChange);
    window.visualViewport?.addEventListener('scroll', onViewportChange);
    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.visualViewport?.removeEventListener('resize', onViewportChange);
      window.visualViewport?.removeEventListener('scroll', onViewportChange);
    };
  }, [allowDesktopPanelResize, reclampFloatingDockLayout]);

  useEffect(() => {
    if (!allowDesktopPanelResize) return;
    reclampFloatingDockLayout();
  }, [allowDesktopPanelResize, reclampFloatingDockLayout, dockDimensions.dockH, dockDimensions.dockW]);

  const dialogIdsToFetch = useMemo(() => {
    const ids = new Set<number>();
    dedupedUnreadPreviewRows.forEach(({ dialog }) => ids.add(dialog.id));
    for (const s of sessions) {
      if (!s.isMinimized) continue;
      if (s.messages?.length) continue;
      const d = normalizeSessionDialogId(s.selectedDialog?.id ?? s.assignedDialogId);
      if (d !== null) ids.add(d);
    }
    return Array.from(ids).sort((a, b) => a - b);
  }, [dedupedUnreadPreviewRows, sessions]);

  const [dialogPreviewLines, setDialogPreviewLines] = useState<Record<number, string>>({});
  const dialogFetchKey = dialogIdsToFetch.join(',');

  useEffect(() => {
    // Electron main: превью сообщений только в popup-окне.
    if (isDesktopMainChatHost) return;
    if (dialogIdsToFetch.length === 0) {
      setDialogPreviewLines({});
      return;
    }

    let cancelled = false;
    const attachmentLabel = t('chat.previewAttachment');

    void (async () => {
      const results = await Promise.all(
        dialogIdsToFetch.map(async (dialogId) => {
          try {
            const res = await DialogsApi.getMessages({
              dialogId: String(dialogId),
              page: 0,
              size: 1,
              sort: 'createdAt,desc',
            });
            const msg = res?.data?.content?.[0];
            return [dialogId, previewLineFromMessagePayload(msg, attachmentLabel)] as const;
          } catch {
            return [dialogId, ''] as const;
          }
        }),
      );

      if (cancelled) return;
      setDialogPreviewLines(Object.fromEntries(results) as Record<number, string>);
    })();

    return () => {
      cancelled = true;
    };
  }, [dialogFetchKey, t, isDesktopMainChatHost]);

  const attachmentLabel = t('chat.previewAttachment');

  const compactMinimizedEntries = useMemo((): CompactMinimizedEntry[] => {
    const minimized = sessions.filter((s) => s.isMinimized && hasRenderableMinimizedContent(s));
    const items: CompactMinimizedEntry[] = [];

    minimized.forEach((session) => {
      const raw = minimizedSessionPreviewRaw(session, dialogPreviewLines, attachmentLabel);
      const subtitle = truncatePreviewLine(raw, 60);
      items.push({
        kind: 'session',
        key: `minimized-${session.id}`,
        sessionId: session.id,
        title:
          session.selectedUserName ||
          session.selectedDialog?.client_name ||
          t('chat.newChatFallback'),
        subtitle: subtitle || undefined,
        unread: effectiveMinimizedSessionUnread(session, dialogsUnreadCounts),
      });
    });

    dedupedUnreadPreviewRows.forEach(({ dialog, sessionId }) => {
      const sessionForDialog = sessions.find((s) => s.id === sessionId);
      const unreadFromSessionMessages = unreadInSessionMessagesByDialog(
        sessionForDialog,
        dialog.id,
      );
      const unreadCountBase = unreadCountForPreviewEntry(dialog, dialogsUnreadCounts);
      const unreadCount = Math.max(unreadCountBase, unreadFromSessionMessages);
      const raw = (dialogPreviewLines[dialog.id] || '').trim();
      const subtitle = truncatePreviewLine(raw, 60);
      items.push({
        kind: 'unread',
        key: `unread-${dialog.id}`,
        sessionId,
        dialog,
        title: dialog.owner.fullName,
        subtitle: subtitle || undefined,
        unread: unreadCount,
      });
    });

    return items;
  }, [
    sessions,
    dedupedUnreadPreviewRows,
    dialogsUnreadCounts,
    dialogPreviewLines,
    attachmentLabel,
    t,
  ]);
  useEffect(() => {
    const previewUnreadBadges = compactMinimizedEntries
      .filter((e): e is Extract<typeof e, { kind: 'unread' }> => e.kind === 'unread')
      .map((e) => ({
        dialogId: e.dialog.id,
        badge: e.unread,
        title: e.title,
      }));
    const listUnreadSum = compactMinimizedEntries.reduce((s, e) => s + e.unread, 0);
    const minimizedListToggleBadge =
      listUnreadSum > 0 ? listUnreadSum : compactMinimizedEntries.length;
    operatorUnreadDebug('Бейджи превью и карта WS по диалогам', {
      всегоПоИконкеЧата: calculateTotalUnread(),
      агрегатUserQueueНеТолькоДиалоги: socketUnreadTotal,
      суммаВКомпактномСписке: minimizedListToggleBadge,
      картаДиалоговWs: unreadMapSnapshot(dialogsUnreadCounts),
      строкиПревьюНеизСписка: previewUnreadBadges,
      свёрнутыеСессии: sessions
        .filter((s) => s.isMinimized)
        .map((s) => ({
          sessionId: s.id,
          бейдж: effectiveMinimizedSessionUnread(s, dialogsUnreadCounts),
          sessionUnreadCount: s.unreadCount ?? 0,
        })),
      последнийWsТип: lastMessage?.type,
    });
  }, [
    compactMinimizedEntries,
    dialogsUnreadCounts,
    socketUnreadTotal,
    sessions,
    lastMessage?.type,
    lastMessage?.destination,
    calculateTotalUnread,
  ]);

  if (!hasChatPermissions) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  const minimizedPreviewRightPx =
    expandedSessions.length > 0
      ? CHAT_FOOTER_RIGHT +
        (allowDesktopPanelResize ? panelSize.w : DEFAULT_CHAT_PANEL.w) +
        MINIMIZED_PREVIEW_GAP_PX
      : CHAT_FOOTER_RIGHT;

  const hasUnreadInCompactList = compactMinimizedEntries.some((e) => e.unread > 0);
  const compactListUnreadSum = compactMinimizedEntries.reduce((s, e) => s + e.unread, 0);
  const minimizedListToggleBadge =
    compactListUnreadSum > 0 ? compactListUnreadSum : compactMinimizedEntries.length;

  return (
    <div className={styles.chatContainer}>
      {/* Кнопки управления: 
          - Desktop (Electron) главное окно: только toggle button (открыть/закрыть popup)
          - Desktop popup окно: только toggle button (остальные кнопки внутри ChatPanel)
          - Web версия: toggle button + new chat button (вертикально)
          - Mobile: кнопки поверх чата 
      */}
      
      {/* Desktop Electron: главное окно - только кнопка открытия чата */}
      {isDesktopMainChatHost && (
        <ChatToggleButton isOperatorChatPopupWindow={false} />
      )}
      
      {/* Desktop popup окно: только toggle button */}
      {isOperatorChatPopupWindow && !allowDesktopPanelResize && (
        <ChatToggleButton isOperatorChatPopupWindow={true} />
      )}

      {/* Web версия (не desktop, не popup): toggle button + new chat вертикально */}
      {!isDesktopMainChatHost && !isOperatorChatPopupWindow && !allowDesktopPanelResize && (
        <>
          <ChatToggleButton isOperatorChatPopupWindow={false} />
          <NewChatButton />
        </>
      )}
      
      {/* Desktop popup: кнопки в свёрнутом режиме */}
      {isOperatorChatPopupWindow && !allowDesktopPanelResize && !isChatLayoutPinned && (
        <>
          {/* Кнопка "открыть в отдельном окне" (вернуться в главное окно) - вверху */}
          <Tooltip title={operatorChatWindowButtonLabel} placement="left">
            <div className={styles.operatorPopupReturnFab}>
              <IconButton
                className={styles.dockOpenWindowButton}
                size="large"
                color="primary"
                onClick={handleOperatorChatWindowButtonClick}
                aria-label={operatorChatWindowButtonLabel}>
                <OpenInBrowserIcon />
              </IconButton>
            </div>
          </Tooltip>
          {/* Кнопка "новый чат" - только если есть сессии с пользователем */}
          <NewChatButton />
          {/* Кнопка toggle */}
          <ChatToggleButton isOperatorChatPopupWindow={true} />
        </>
      )}
      {isCompactMinimizedUi && !isOperatorChatPopupWindow && compactMinimizedEntries.length > 0 && (
        <>
          <Tooltip title={t('chat.minimizedListTooltip')} placement="left">
            <div className={styles.showMinimizedButtonWrapper}>
              <IconButton
                className={styles.showMinimizedButton}
                onClick={() => setMinimizedListOpen(true)}
                color="primary"
                size="large"
                aria-label={t('chat.minimizedListTooltip')}>
                <ViewListIcon />
              </IconButton>
              <span
                className={
                  hasUnreadInCompactList
                    ? styles.minimizedListCountBadgeUnread
                    : styles.minimizedListCountBadge
                }
                aria-hidden>
                {minimizedListToggleBadge > 99 ? '99+' : minimizedListToggleBadge}
              </span>
            </div>
          </Tooltip>
          <Drawer
            anchor="bottom"
            open={minimizedListOpen}
            onClose={() => setMinimizedListOpen(false)}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: '55vh',
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                },
              },
            }}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('chat.minimizedListTitle')}
              </Typography>
            </Box>
            <List dense disablePadding sx={{ pb: 2, px: 0.5 }}>
              {compactMinimizedEntries.map((entry) => (
                <ListItemButton
                  key={entry.key}
                  sx={{ alignItems: 'flex-start', gap: 1, py: 1.25 }}
                  onClick={() => {
                    setMinimizedListOpen(false);
                    if (entry.kind === 'session') {
                      handleExpandSession(entry.sessionId);
                    } else {
                      setJustExpandedSessionId(entry.sessionId);
                      void openUnreadDialog(entry.sessionId, entry.dialog);
                    }
                  }}>
                  <ListItemText
                    sx={{ flex: '1 1 auto', minWidth: 0, my: 0 }}
                    primary={entry.title}
                    secondary={entry.subtitle}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      bgcolor: entry.unread > 0 ? 'error.main' : 'action.selected',
                      color: entry.unread > 0 ? 'error.contrastText' : 'text.secondary',
                      borderRadius: '10px',
                      px: 0.75,
                      py: 0.25,
                      fontWeight: 600,
                      minWidth: 22,
                      textAlign: 'center',
                      lineHeight: 1.5,
                      mt: 0.25,
                    }}>
                    {entry.unread > 99 ? '99+' : entry.unread}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Drawer>
        </>
      )}

      {allowDesktopPanelResize ? (
        <div
          className={`${styles.chatFloatingDock} ${isDockDragging ? styles.chatFloatingDockDragging : ''}`}
          style={{ right: dockRightPx, bottom: dockBottomPx }}
          {...(isOperatorChatPopupWindow
            ? {
                'data-operator-chat-dock': '1',
                'data-operator-chat-preview-count':
                  minimizedSessions.length + dedupedUnreadPreviewRows.length,
              }
            : {})}>
          <div className={styles.dockFabColumn}>
            {/* Кнопка "новый чат" - только если есть сессии с пользователем (внизу колонки) */}
            <NewChatButton />
            {/* Кнопка toggle - в dock для web-версии */}
            <ChatToggleButton isOperatorChatPopupWindow={false} />
            {/* Кнопка "открыть в отдельном окне" для web-версии (вверху) */}
            {!isChatLayoutPinned && !isDesktopShell && !isOperatorChatPopupWindow && (
              <Tooltip title={operatorChatWindowButtonLabel} placement="left">
                <IconButton
                  className={styles.dockOpenWindowButton}
                  size="large"
                  color="primary"
                  onClick={handleOperatorChatWindowButtonClick}
                  aria-label={operatorChatWindowButtonLabel}>
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div className={styles.dockPanelsColumn}>
            {expandedSessionsForDock.map((session) => (
              <ChatFooterResizableFrame
                key={`expanded-${session.id}`}
                docked
                resizeDisabled={isChatLayoutPinned || isBrowserDetachedChatPopup}
                size={panelSize}
                onSizeLiveChange={(s) => {
                  if (isChatLayoutPinned || isBrowserDetachedChatPopup) return;
                  setPanelSize(resolvePanelSize(s));
                }}
                onSizeCommit={handlePanelSizeCommit}
                minSize={MIN_CHAT_PANEL}
                getMaxSize={getMaxPanelSize}>
                <Card
                  className={`${styles.chatFooter} ${styles.expanded} ${styles.chatFooterFluid}`}
                  sx={{
                    '--chat-ui-scale': String(chatUiScale),
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}>
                  <ChatPanel
                    sessionId={session.id}
                    onMinimize={() => handleToggleSessionMinimize(session.id)}
                    scrollToBottomOnExpand={justExpandedSessionId === session.id}
                    onScrollToBottomDone={handleScrollToBottomDone}
                    dockDragEnabled={!isChatLayoutPinned && !isOperatorChatPopupWindow}
                    onDockDragPointerDown={handleDockDragPointerDown}
                    chatLayoutPinned={isChatLayoutPinned}
                    onToggleChatLayoutPin={handleToggleChatLayoutPin}
                    onCloseAllChats={
                      isOperatorChatPopupWindow ? closeOperatorChatPopupAndRestoreMain : undefined
                    }
                    closeAllChatsTitle={
                      isOperatorChatPopupWindow ? 'Закрыть диалоговое окно' : undefined
                    }
                  />
                </Card>
              </ChatFooterResizableFrame>
            ))}
          </div>
          <div className={`${styles.minimizedChats} ${styles.minimizedChatsDocked}`}>
            {minimizedSessions.map((session, index) => {
              const previewLine = truncatePreviewLine(
                minimizedSessionPreviewRaw(session, dialogPreviewLines, attachmentLabel),
                30,
              );
              const minimizedUnread = effectiveMinimizedSessionUnread(
                session,
                dialogsUnreadCounts,
              );
              return (
                <div
                  key={`minimized-${session.id}`}
                  data-operator-chat-preview="1"
                  className={`${styles.minimizedChat} ${minimizedUnread > 0 ? styles.hasUnread : ''}`}
                  style={{ zIndex: 1000 - index }}
                  onClick={() => handleExpandSession(session.id)}>
                  <div className={styles.minimizedHeader}>
                    <span>
                      {session.selectedUserName ||
                        session.selectedDialog?.client_name ||
                        t('chat.newChatFallback')}
                    </span>
                    <span className={styles.unreadBadge}>
                      {minimizedUnread > 99 ? '99+' : minimizedUnread}
                    </span>
                  </div>
                  {previewLine ? <div className={styles.lastMessage}>{previewLine}</div> : null}
                </div>
              );
            })}

            {dedupedUnreadPreviewRows.map(({ dialog, sessionId }, index) => {
              const sessionForDialog = sessions.find((s) => s.id === sessionId);
              const unreadFromSessionMessages = unreadInSessionMessagesByDialog(
                sessionForDialog,
                dialog.id,
              );
              const unreadCountBase = unreadCountForPreviewEntry(dialog, dialogsUnreadCounts);
              const unreadCount = Math.max(unreadCountBase, unreadFromSessionMessages);
              const raw = (dialogPreviewLines[dialog.id] || '').trim();
              const line = truncatePreviewLine(raw, 30);

              return (
                <div
                  key={`unread-${dialog.id}`}
                  data-operator-chat-preview="1"
                  className={`${styles.minimizedChat} ${styles.unreadDialog} ${
                    unreadCount > 0 ? styles.hasUnread : ''
                  }`}
                  style={{ zIndex: 1000 - (minimizedSessions.length + index) }}
                  onClick={async () => {
                    setJustExpandedSessionId(sessionId);
                    await openUnreadDialog(sessionId, dialog);
                  }}>
                  <div className={styles.minimizedHeader}>
                    <span>{dialog.owner.fullName}</span>
                    <span className={styles.unreadBadge}>{unreadCount}</span>
                  </div>
                  {line ? <div className={styles.lastMessage}>{line}</div> : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          {!isDesktopMainChatHost ? (
            <div className={styles.minimizedChats}>
              {minimizedSessions.map((session, index) => {
              const previewLine = truncatePreviewLine(
                minimizedSessionPreviewRaw(session, dialogPreviewLines, attachmentLabel),
                30,
              );
              const minimizedUnread = effectiveMinimizedSessionUnread(
                session,
                dialogsUnreadCounts,
              );
              return (
                <div
                  key={`minimized-${session.id}`}
                  className={`${styles.minimizedChat} ${minimizedUnread > 0 ? styles.hasUnread : ''}`}
                  style={{
                    bottom: `${120 + index * 60}px`,
                    right: `${minimizedPreviewRightPx}px`,
                    zIndex: 1000 - index,
                  }}
                  onClick={() => handleExpandSession(session.id)}>
                  <div className={styles.minimizedHeader}>
                    <span>
                      {session.selectedUserName ||
                        session.selectedDialog?.client_name ||
                        t('chat.newChatFallback')}
                    </span>
                    <span className={styles.unreadBadge}>
                      {minimizedUnread > 99 ? '99+' : minimizedUnread}
                    </span>
                  </div>
                  {previewLine ? <div className={styles.lastMessage}>{previewLine}</div> : null}
                </div>
              );
            })}

            {dedupedUnreadPreviewRows.map(({ dialog, sessionId }, index) => {
              const sessionForDialog = sessions.find((s) => s.id === sessionId);
              const unreadFromSessionMessages = unreadInSessionMessagesByDialog(
                sessionForDialog,
                dialog.id,
              );
              const unreadCountBase = unreadCountForPreviewEntry(dialog, dialogsUnreadCounts);
              const unreadCount = Math.max(unreadCountBase, unreadFromSessionMessages);
              const raw = (dialogPreviewLines[dialog.id] || '').trim();
              const line = truncatePreviewLine(raw, 30);

              return (
                <div
                  key={`unread-${dialog.id}`}
                  className={`${styles.minimizedChat} ${styles.unreadDialog} ${
                    unreadCount > 0 ? styles.hasUnread : ''
                  }`}
                  style={{
                    bottom: `${120 + (minimizedSessions.length + index) * 60}px`,
                    right: `${minimizedPreviewRightPx}px`,
                    zIndex: 1000 - (minimizedSessions.length + index),
                  }}
                  onClick={async () => {
                    setJustExpandedSessionId(sessionId);
                    await openUnreadDialog(sessionId, dialog);
                  }}>
                  <div className={styles.minimizedHeader}>
                    <span>{dialog.owner.fullName}</span>
                    <span className={styles.unreadBadge}>{unreadCount}</span>
                  </div>
                  {line ? <div className={styles.lastMessage}>{line}</div> : null}
                </div>
              );
            })}
            </div>
          ) : null}

          {/* Развёрнутые сессии в режиме mobile/web/desktop popup: позиционируем их абсолютно */}
          {!isDesktopMainChatHost
            ? expandedSessions.map((session, index) => {
                // Для mobile/web/desktop popup: развёрнутые чаты позиционируем друг над другом с z-index
                // Последний (активный) чат будет сверху
                const chatIndex = expandedSessions.length - 1 - index;
                return (
                  <Card
                    key={`expanded-${session.id}`}
                    className={`${styles.chatFooter} ${styles.expanded}`}
                    sx={{
                      // Позиционируем каждый чат со смещением, чтобы они не перекрывали друг друга полностью
                      bottom: `${CHAT_FOOTER_BOTTOM + chatIndex * 24}px`,
                      right: `${CHAT_FOOTER_RIGHT + chatIndex * 24}px`,
                      zIndex: 1000 + chatIndex,
                    }}>
                    <ChatPanel
                      sessionId={session.id}
                      onMinimize={() => handleToggleSessionMinimize(session.id)}
                      scrollToBottomOnExpand={justExpandedSessionId === session.id}
                      onScrollToBottomDone={handleScrollToBottomDone}
                      // Для desktop popup добавляем кнопку закрытия
                      onCloseAllChats={
                        isOperatorChatPopupWindow ? closeOperatorChatPopupAndRestoreMain : undefined
                      }
                      closeAllChatsTitle={
                        isOperatorChatPopupWindow ? 'Закрыть диалоговое окно' : undefined
                      }
                    />
                  </Card>
                );
              })
            : null}
        </>
      )}
    </div>
  );
};

const ChatFooter = () => {
  return (
    <SocketProvider stompConnect>
      <ChatProvider>
        <ChatContainer />
      </ChatProvider>
    </SocketProvider>
  );
};

export default ChatFooter;
